import { computed, ref } from 'vue'
import { buildCasLoginUrl, saveLoginReturnTo, parseAuthSessionPayload } from '@/auth/session'
import { authenticationRecovery } from '@/auth/recovery'
import { AUTH_POPUP_CLOSE_EVENT, AUTH_POPUP_SUCCESS_EVENT, AUTH_POPUP_WINDOW_NAME } from '@/auth/popupProtocol'

const popupBlocked = ref(false)
let loginPopup: Window | null = null
let attemptSignal: AbortSignal | null = null
let popupClosedTimer: number | null = null
let listening = false

function clearPopupTimer() {
  if (popupClosedTimer !== null) window.clearInterval(popupClosedTimer)
  popupClosedTimer = null
}

function startLoginPopup(returnTo?: string) {
  if (authenticationRecovery.busy.value) {
    loginPopup?.focus()
    return
  }
  popupBlocked.value = false
  saveLoginReturnTo(returnTo)
  attemptSignal = authenticationRecovery.beginAuthentication()
  attemptSignal.addEventListener('abort', () => {
    clearPopupTimer()
    loginPopup?.close()
    loginPopup = null
  }, { once: true })
  try {
    const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - 520) / 2))
    const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - 760) / 2))
    loginPopup = window.open(buildCasLoginUrl('popup'), AUTH_POPUP_WINDOW_NAME,
      `popup=yes,width=520,height=760,left=${left},top=${top},resizable=yes,scrollbars=yes`)
    if (!loginPopup) {
      popupBlocked.value = true
      throw new Error('Popup was blocked by the browser. Allow popups and try again.')
    }
    clearPopupTimer()
    popupClosedTimer = window.setInterval(() => {
      if (loginPopup && !loginPopup.closed) return
      clearPopupTimer()
      loginPopup = null
      if (attemptSignal) authenticationRecovery.failAuthentication(attemptSignal, new Error('Login window closed before sign-in completed.'))
    }, 250)
  } catch (error) {
    authenticationRecovery.failAuthentication(attemptSignal, error)
  }
}

function cancelLoginPopup() {
  clearPopupTimer()
  loginPopup?.close()
  loginPopup = null
  popupBlocked.value = false
  authenticationRecovery.dismissAuthentication()
}

async function handleAuthPopupMessage(event: MessageEvent) {
  if (event.origin !== window.location.origin || event.source !== loginPopup || !loginPopup) return
  if (event.data?.type !== AUTH_POPUP_SUCCESS_EVENT || !attemptSignal) return
  const signal = attemptSignal
  const source = loginPopup
  clearPopupTimer()
  loginPopup = null
  source.postMessage({ type: AUTH_POPUP_CLOSE_EVENT }, window.location.origin)
  try {
    await authenticationRecovery.completeAuthentication(signal, parseAuthSessionPayload(event.data.payload))
  } catch (error) {
    authenticationRecovery.failAuthentication(signal, error)
  }
}

/** Browser transport only. Completion and errors belong to the auth lifecycle. */
export function useAuthPopupRecovery() {
  if (!listening && typeof window !== 'undefined') {
    window.addEventListener('message', handleAuthPopupMessage)
    listening = true
  }
  return {
    loginInProgress: authenticationRecovery.busy,
    popupBlocked,
    loginError: computed(() => {
      const state = authenticationRecovery.state.value
      return state.phase === 'failed' ? state.error : null
    }),
    startLoginPopup,
    cancelLoginPopup,
  }
}
